import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from '../hooks/use-toast';
import { 
  GraduationCap, Building, Users, Globe, Mail, Phone, MapPin,
  CheckCircle, Loader2, FileText, Award, TrendingUp
} from 'lucide-react';
import api from '../utils/api';

const CollegeMembership = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [formData, setFormData] = useState({
    // Institution Information
    institution_name: '',
    institution_type: '',
    location_city: '',
    location_state: '',
    location_country: 'United States',
    website: '',
    ceeb_code: '',
    
    // Contact Information
    contact_name: '',
    contact_title: '',
    contact_email: '',
    contact_phone: '',
    
    // Institution Details
    enrollment_size: '',
    acceptance_rate: '',
    tuition_in_state: '',
    tuition_out_state: '',
    application_fee: '',
    
    // Programs
    offers_undergraduate: true,
    offers_graduate: false,
    popular_majors: '',
    special_programs: '',
    
    // Application Requirements
    requires_sat_act: '',
    requires_essay: true,
    requires_recommendations: true,
    recommendation_count: '2',
    
    // Deadlines
    regular_deadline: '',
    early_decision_deadline: '',
    early_action_deadline: '',
    
    // Additional
    why_join: '',
    additional_info: '',
    agree_terms: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agree_terms) {
      toast({ title: 'Error', description: 'Please agree to the terms and conditions', variant: 'destructive' });
      return;
    }
    
    if (!formData.institution_name || !formData.contact_email) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/university-hub/membership/apply', {
        institution_name: formData.institution_name,
        institution_type: formData.institution_type,
        location_city: formData.location_city,
        location_state: formData.location_state,
        location_country: formData.location_country,
        website: formData.website,
        ceeb_code: formData.ceeb_code || undefined,
        contact_name: formData.contact_name,
        contact_title: formData.contact_title,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone || undefined,
        enrollment_size: formData.enrollment_size ? parseInt(formData.enrollment_size) : undefined,
        acceptance_rate: formData.acceptance_rate ? parseFloat(formData.acceptance_rate) : undefined,
        application_fee: formData.application_fee ? parseInt(formData.application_fee) : undefined,
        tuition_in_state: formData.tuition_in_state ? parseInt(formData.tuition_in_state) : undefined,
        tuition_out_state: formData.tuition_out_state ? parseInt(formData.tuition_out_state) : undefined,
        popular_majors: formData.popular_majors || undefined,
        requires_sat_act: formData.requires_sat_act || undefined,
        regular_deadline: formData.regular_deadline || undefined,
        early_decision_deadline: formData.early_decision_deadline || undefined,
        early_action_deadline: formData.early_action_deadline || undefined,
        why_join: formData.why_join || undefined
      });
      setSubmitted(true);
      setReferenceNumber(response.data.reference_number);
      toast({ title: 'Success', description: 'Your partnership application has been submitted!' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail 
        : Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ')
        : 'Failed to submit application. Please try again.';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card className="text-center py-12">
            <CardContent>
              <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in joining Wayo. We will review your application
                and get back to you by June 29. We appreciate your patience.
              </p>
              <p className="text-sm text-gray-500">
                Reference Number: {referenceNumber || 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="bg-white/20 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Building className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Join the Wayo Network
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Connect with over 1 million motivated students in South Asia region. Streamline your admissions process with validation of documents submitted through in-house, and third party sources and reach qualified applicants from around the world.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center mb-8">Why Partner with Wayo?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold mb-2">South Asian Students</h3>
              <p className="text-sm text-gray-600">Access motivated applicants from the South Asia region</p>
            </div>
            <div className="text-center p-6">
              <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Globe className="h-7 w-7 text-teal-600" />
              </div>
              <h3 className="font-semibold mb-2">South Asia Region</h3>
              <p className="text-sm text-gray-600">Focused reach in key international markets</p>
            </div>
            <div className="text-center p-6">
              <div className="bg-cyan-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText className="h-7 w-7 text-cyan-600" />
              </div>
              <h3 className="font-semibold mb-2">Streamlined Process</h3>
              <p className="text-sm text-gray-600">Standardized applications save time</p>
            </div>
            <div className="text-center p-6">
              <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-7 w-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold mb-2">Data Insights</h3>
              <p className="text-sm text-gray-600">Analytics to improve your recruitment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl">College Membership Application</CardTitle>
              <CardDescription>
                Complete this form to apply for Wayo membership. Fields marked with * are required.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Institution Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building className="h-5 w-5 text-emerald-600" />
                    Institution Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Institution Name *</Label>
                      <Input 
                        value={formData.institution_name}
                        onChange={(e) => handleChange('institution_name', e.target.value)}
                        placeholder="e.g., University of California, Berkeley"
                        required
                      />
                    </div>
                    <div>
                      <Label>Institution Type *</Label>
                      <Select value={formData.institution_type} onValueChange={(v) => handleChange('institution_type', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public_university">Public University</SelectItem>
                          <SelectItem value="private_university">Private University</SelectItem>
                          <SelectItem value="liberal_arts">Liberal Arts College</SelectItem>
                          <SelectItem value="community_college">Community College</SelectItem>
                          <SelectItem value="technical">Technical/Vocational</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>CEEB/College Board Code</Label>
                      <Input 
                        value={formData.ceeb_code}
                        onChange={(e) => handleChange('ceeb_code', e.target.value)}
                        placeholder="e.g., 4833"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input 
                        value={formData.location_city}
                        onChange={(e) => handleChange('location_city', e.target.value)}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <Label>State/Province *</Label>
                      <Input 
                        value={formData.location_state}
                        onChange={(e) => handleChange('location_state', e.target.value)}
                        placeholder="State"
                        required
                      />
                    </div>
                    <div>
                      <Label>Country</Label>
                      <Input 
                        value={formData.location_country}
                        onChange={(e) => handleChange('location_country', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Website *</Label>
                      <Input 
                        type="url"
                        value={formData.website}
                        onChange={(e) => handleChange('website', e.target.value)}
                        placeholder="https://www.university.edu"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Mail className="h-5 w-5 text-emerald-600" />
                    Primary Contact
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Contact Name *</Label>
                      <Input 
                        value={formData.contact_name}
                        onChange={(e) => handleChange('contact_name', e.target.value)}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Title/Position *</Label>
                      <Input 
                        value={formData.contact_title}
                        onChange={(e) => handleChange('contact_title', e.target.value)}
                        placeholder="e.g., Director of Admissions"
                        required
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input 
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        placeholder="admissions@university.edu"
                        required
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) => handleChange('contact_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Institution Details */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Award className="h-5 w-5 text-emerald-600" />
                    Institution Details
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Undergraduate Enrollment</Label>
                      <Input 
                        type="number"
                        value={formData.enrollment_size}
                        onChange={(e) => handleChange('enrollment_size', e.target.value)}
                        placeholder="e.g., 15000"
                      />
                    </div>
                    <div>
                      <Label>Acceptance Rate (%)</Label>
                      <Input 
                        type="number"
                        value={formData.acceptance_rate}
                        onChange={(e) => handleChange('acceptance_rate', e.target.value)}
                        placeholder="e.g., 25"
                      />
                    </div>
                    <div>
                      <Label>Application Fee ($)</Label>
                      <Input 
                        type="number"
                        value={formData.application_fee}
                        onChange={(e) => handleChange('application_fee', e.target.value)}
                        placeholder="e.g., 75"
                      />
                    </div>
                    <div>
                      <Label>In-State Tuition ($)</Label>
                      <Input 
                        type="number"
                        value={formData.tuition_in_state}
                        onChange={(e) => handleChange('tuition_in_state', e.target.value)}
                        placeholder="Annual tuition"
                      />
                    </div>
                    <div>
                      <Label>Out-of-State Tuition ($)</Label>
                      <Input 
                        type="number"
                        value={formData.tuition_out_state}
                        onChange={(e) => handleChange('tuition_out_state', e.target.value)}
                        placeholder="Annual tuition"
                      />
                    </div>
                    <div>
                      <Label>SAT/ACT Policy</Label>
                      <Select value={formData.requires_sat_act} onValueChange={(v) => handleChange('requires_sat_act', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select policy" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="required">Required</SelectItem>
                          <SelectItem value="optional">Test Optional</SelectItem>
                          <SelectItem value="blind">Test Blind</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Popular Majors/Programs</Label>
                    <Textarea 
                      value={formData.popular_majors}
                      onChange={(e) => handleChange('popular_majors', e.target.value)}
                      placeholder="List your most popular majors and programs..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Deadlines */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Application Deadlines
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Regular Decision Deadline</Label>
                      <Input 
                        type="date"
                        value={formData.regular_deadline}
                        onChange={(e) => handleChange('regular_deadline', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Early Decision Deadline</Label>
                      <Input 
                        type="date"
                        value={formData.early_decision_deadline}
                        onChange={(e) => handleChange('early_decision_deadline', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Early Action Deadline</Label>
                      <Input 
                        type="date"
                        value={formData.early_action_deadline}
                        onChange={(e) => handleChange('early_action_deadline', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Why Join */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Why Join Wayo?</h3>
                  <Textarea 
                    value={formData.why_join}
                    onChange={(e) => handleChange('why_join', e.target.value)}
                    placeholder="Tell us why your institution wants to join Wayo and how you plan to use our platform..."
                    rows={4}
                  />
                </div>

                {/* Terms */}
                <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Checkbox 
                    id="terms"
                    checked={formData.agree_terms}
                    onCheckedChange={(checked) => handleChange('agree_terms', checked)}
                  />
                  <div>
                    <Label htmlFor="terms" className="font-medium">
                      I agree to the Wayo Membership Terms and Conditions *
                    </Label>
                    <p className="text-sm text-gray-600 mt-1">
                      By submitting this application, I confirm that the information provided is accurate
                      and that I am authorized to represent this institution.
                    </p>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full py-6 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
                  {loading ? (
                    <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Submitting...</>
                  ) : (
                    <><GraduationCap className="h-5 w-5 mr-2" /> Submit Application</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CollegeMembership;
